import { useMutation } from "@tanstack/react-query";
import { financialDocumentContract } from "@common/contracts/financial-document.contract";
import { FinancialDocumentExtendedDto } from "@common/dto/financialDocument/financial-document-extended.dto";
import {
    DocumentTaxonomyExtraction,
    FinancialDocumentCounterparty,
    FinancialDocumentExtended,
    FinancialDocumentListItem,
    isFinancialDocumentExtended,
} from "@common/types/financialDocument";
import { FinancialAccount } from "@common/types/financialAccount";
import { DocumentCategory as DocumentCategoryType } from "@common/types/documentCategory";
import { EntityWithUser } from "@common/types/entity";
import { TransactionMatch } from "@common/types/transactionMatch";
import {
    changeEntityForFinancialDocument,
    createLinkFinancialDocumentToFinancialTransaction,
    detachDocumentFromFinancialTransaction,
} from "@lib/financialDocument";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useFinancialDocumentsQuery } from "@components/documents/useFinancialDocumentsQuery";
import { projectFinancialDocumentListItem } from "@components/documents/projectFinancialDocumentListItem";
import {
    financialDocumentQueryKeys,
    patchFinancialDocumentDetailCaches,
} from "@components/documents/lib";
import { queryClient } from "../queryClient";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";

const financialDocumentContractClient = getBackendAPIClient(
    financialDocumentContract,
);

type FetchFinancialQueryResult = ReturnType<
    typeof useFinancialDocumentsQuery
>["data"];

function toListCacheDocument(
    document: FinancialDocumentExtended | FinancialDocumentListItem,
): FinancialDocumentListItem {
    if (isFinancialDocumentExtended(document)) {
        return projectFinancialDocumentListItem(document);
    }

    return document;
}

function updateDocument(
    updatedDocument: FinancialDocumentExtended | FinancialDocumentListItem,
    detailPatch?: Partial<FinancialDocumentExtended>,
) {
    const listDocument = toListCacheDocument(updatedDocument);
    queryClient.setQueriesData<FetchFinancialQueryResult>(
        { queryKey: financialDocumentQueryKeys.lists() },
        (oldData) => {
            if (!oldData) {
                return;
            }

            return {
                ...oldData,
                data: oldData.data.map((document) =>
                    document.id === listDocument.id ? listDocument : document,
                ),
            };
        },
    );

    const resolvedDetailPatch =
        detailPatch ??
        (isFinancialDocumentExtended(updatedDocument)
            ? updatedDocument
            : undefined);

    if (resolvedDetailPatch) {
        patchFinancialDocumentDetailCaches(
            queryClient,
            updatedDocument.id,
            resolvedDetailPatch,
        );
    }
}

export function useDetachFinancialDocumentMutation(
    financialDocument: FinancialDocumentExtended,
    transactionMatch: TransactionMatch,
) {
    const originalMatches = financialDocument.transactionMatches;
    const { isViewOnly } = useViewOnlyRefusal();

    return useMutation({
        mutationFn: async () =>
            await detachDocumentFromFinancialTransaction(
                financialDocument.id,
                transactionMatch.id,
            ),
        async onMutate() {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            await queryClient.cancelQueries({
                queryKey: financialDocumentQueryKeys.details(),
            });

            const transactionMatches =
                financialDocument.transactionMatches.filter(
                    (tm) => tm.id !== transactionMatch.id,
                );

            updateDocument(
                {
                    ...financialDocument,
                    transactionMatches,
                },
                { transactionMatches },
            );

            return financialDocument;
        },
        async onSuccess() {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });
        },
        onError() {
            if (financialDocument) {
                updateDocument(financialDocument, {
                    transactionMatches: originalMatches,
                });
            }
        },
    });
}

interface ChangeEntityMutationVariables {
    financialDocument: FinancialDocumentListItem;
    newEntity: EntityWithUser;
}

export function useChangeEntityMutation() {
    const { isViewOnly } = useViewOnlyRefusal();
    return useMutation({
        mutationFn: async ({
            financialDocument,
            newEntity,
        }: ChangeEntityMutationVariables) =>
            await changeEntityForFinancialDocument({
                financialDocumentId: financialDocument.id,
                entityId: newEntity.id,
            }),

        async onMutate({ financialDocument, newEntity }) {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            await queryClient.cancelQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });

            const updatedDocument = {
                ...financialDocument,
                entity: newEntity,
            };

            updateDocument(updatedDocument);

            return financialDocument;
        },
        async onSuccess() {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });
        },
        onError(_, __, financialDocument) {
            if (financialDocument) {
                updateDocument(financialDocument);
            }
        },
    });
}

export function useCreateLinkFinancialDocumentToFinancialTransactionMutation(
    financialDocument: FinancialDocumentExtended,
    onLinked?: () => Promise<unknown> | void,
) {
    return useMutation({
        mutationFn: (financialTransactionId: number) =>
            createLinkFinancialDocumentToFinancialTransaction({
                financialDocumentId: financialDocument.id,
                financialTransactionId,
            }),
        async onSuccess() {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });

            await onLinked?.();
        },
    });
}

interface UpdateDocumentCategoryVariables {
    document: FinancialDocumentListItem;
    documentCategory: DocumentCategoryType | null;
}

export function useUpdateDocumentCategoryMutation() {
    const { isViewOnly } = useViewOnlyRefusal();
    return useMutation({
        mutationFn: async ({
            document,
            documentCategory,
        }: UpdateDocumentCategoryVariables) => {
            const result =
                await financialDocumentContractClient.updateDocumentCategory({
                    params: { financialDocumentId: document.id },
                    body: {
                        documentCategoryId: documentCategory?.id ?? null,
                    },
                });

            return FinancialDocumentExtendedDto.deserialize(result.body);
        },
        async onMutate({ document, documentCategory }) {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            await queryClient.cancelQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });

            const documentCategoryId = documentCategory?.id ?? null;

            updateDocument({
                ...document,
                documentCategoryId,
                documentCategory: documentCategory ?? null,
                taxonomyExtraction: documentCategoryId
                    ? {
                          ...(document.taxonomyExtraction ?? {
                              periodStartDate: null,
                              periodEndDate: null,
                              accountNumber: null,
                              institutionName: null,
                              otherAccountIdentifiers: null,
                          }),
                      }
                    : null,
            });

            return document;
        },
        async onSuccess() {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });
        },
        onError(_, __, previousDocument) {
            if (previousDocument) {
                updateDocument(previousDocument);
            }
        },
    });
}

interface UpdateDocumentCounterpartyVariables {
    document: FinancialDocumentListItem;
    counterparty: FinancialDocumentCounterparty | null;
}

export function useUpdateDocumentCounterpartyMutation() {
    const { isViewOnly } = useViewOnlyRefusal();
    return useMutation({
        mutationFn: async ({
            document,
            counterparty,
        }: UpdateDocumentCounterpartyVariables) => {
            const result =
                await financialDocumentContractClient.updateDocumentCounterparty(
                    {
                        params: { financialDocumentId: document.id },
                        body: {
                            counterpartyId: counterparty?.id ?? null,
                        },
                    },
                );

            return FinancialDocumentExtendedDto.deserialize(result.body);
        },
        async onMutate({ document, counterparty }) {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            await queryClient.cancelQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });

            updateDocument({
                ...document,
                counterpartyId: counterparty?.id ?? null,
                counterparty: counterparty ?? null,
            });

            return document;
        },
        async onSuccess() {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });
        },
        onError(_, __, previousDocument) {
            if (previousDocument) {
                updateDocument(previousDocument);
            }
        },
    });
}

interface ChangeFinancialAccountVariables {
    financialDocument: FinancialDocumentListItem;
    financialAccount: FinancialAccount | null;
}

export function useChangeFinancialAccountMutation() {
    const { isViewOnly } = useViewOnlyRefusal();
    return useMutation({
        mutationFn: async ({
            financialDocument,
            financialAccount,
        }: ChangeFinancialAccountVariables) => {
            await financialDocumentContractClient.updateDocumentMetadata({
                params: { financialDocumentId: financialDocument.id },
                body: {
                    financialAccountId: financialAccount?.id ?? null,
                },
            });
        },
        async onMutate({ financialDocument, financialAccount }) {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            await queryClient.cancelQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });

            updateDocument({
                ...financialDocument,
                financialAccountId: financialAccount?.id ?? null,
                financialAccount,
            });

            return financialDocument;
        },
        async onSuccess() {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });
        },
        onError(_, __, previousDocument) {
            if (previousDocument) {
                updateDocument(previousDocument);
            }
        },
    });
}

interface UpdatePeriodVariables {
    financialDocument: FinancialDocumentListItem;
    periodStartDate: string | null;
    periodEndDate: string | null;
}

function buildTaxonomyExtraction({
    financialDocument,
    periodStartDate,
    periodEndDate,
}: UpdatePeriodVariables): DocumentTaxonomyExtraction {
    const existing = financialDocument.taxonomyExtraction;
    return {
        periodStartDate,
        periodEndDate,
        accountNumber: existing?.accountNumber ?? null,
        institutionName: existing?.institutionName ?? null,
        otherAccountIdentifiers: existing?.otherAccountIdentifiers ?? null,
    };
}

export function useUpdatePeriodMutation() {
    const { isViewOnly } = useViewOnlyRefusal();
    return useMutation({
        mutationFn: async (variables: UpdatePeriodVariables) => {
            await financialDocumentContractClient.updateTaxonomyExtraction({
                params: {
                    financialDocumentId: variables.financialDocument.id,
                },
                body: buildTaxonomyExtraction(variables),
            });
        },
        async onMutate(variables) {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            await queryClient.cancelQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });

            updateDocument({
                ...variables.financialDocument,
                taxonomyExtraction: buildTaxonomyExtraction(variables),
            });

            return variables.financialDocument;
        },
        async onSuccess() {
            await queryClient.invalidateQueries({
                queryKey: financialDocumentQueryKeys.all(),
            });
        },
        onError(_, __, previousDocument) {
            if (previousDocument) {
                updateDocument(previousDocument);
            }
        },
    });
}
